import { Directive, ElementRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** Propriedades do `vivest-switch` (design system) usadas pela integração com formulários. */
type VivestSwitchElement = HTMLElement & { checked: boolean; disabled: boolean };

/**
 * Liga o `<vivest-switch>` do design system aos formulários do Angular (`formControlName`,
 * `formControl` e `ngModel`). O web component expõe `checked`/`disabled` como propriedades e
 * dispara `change` ao ser ligado ou desligado, mas não implementa `ControlValueAccessor`.
 *
 * @example
 * <vivest-switch formControlName="disponivel" aria-label="Disponível para participante"></vivest-switch>
 */
@Directive({
  selector: 'vivest-switch[formControlName], vivest-switch[formControl], vivest-switch[ngModel]',
  standalone: true,
  host: {
    '(change)': 'aoAlterar()',
    '(focusout)': 'aoSair()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VivestSwitchValueAccessorDirective),
      multi: true,
    },
  ],
})
export class VivestSwitchValueAccessorDirective implements ControlValueAccessor {
  private readonly elemento = inject<ElementRef<VivestSwitchElement>>(ElementRef).nativeElement;

  private onChange: (valor: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(valor: boolean | null): void {
    this.elemento.checked = !!valor;
  }

  registerOnChange(fn: (valor: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.elemento.disabled = desabilitado;
  }

  aoAlterar(): void {
    this.onChange(this.elemento.checked);
  }

  aoSair(): void {
    this.onTouched();
  }
}
